import React, { useMemo, useCallback, useState } from 'react';
import { Container, Card, Spinner, Alert } from 'react-bootstrap';
import { toast } from 'sonner';
import AdminLayout from '../../../layouts/AdminLayout';
import useGetAllRoomType from '../../../services/room-type/hooks/useGetAllRoomType';
import { RoomType } from '../../../services/room-type/response/GetAllRoomTypeResponse';

// --- Tipe Data ---
interface OtherLink {
    name: string;
}

interface IcsLinkItemData {
    id: string; // ID sekarang string (UUID) dari API
    title: string;
    icsLink: string;
    otherLinks: OtherLink[];
}

// --- Komponen Anak (Tidak ada perubahan di sini) ---
const LinkRow: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
    <div className="d-flex flex-wrap align-items-center gap-2 mb-2">
        <strong style={{ minWidth: '180px' }}>{label}:</strong>
        {children}
    </div>
);

const IcsLinkItem: React.FC<{ item: IcsLinkItemData; index: number }> = ({ item, index }) => {
    const [copyButtonText, setCopyButtonText] = useState('Copy');

    const handleCopy = useCallback(async () => {
        try {
            await navigator.clipboard.writeText(item.icsLink);
            setCopyButtonText('Copied!');
            toast.success('Link copied to clipboard!');
            setTimeout(() => setCopyButtonText('Copy'), 2000);
        } catch (err) {
            console.error('Failed to copy text: ', err);
            toast.error('Failed to copy link.');
        }
    }, [item.icsLink]);

    // const handleAddLink = (linkName: string) => {
    //     toast.info(`'Add ${linkName}' functionality is not implemented yet.`);
    // };

    return (
        <div className="p-3 p-md-4 border-bottom">
            <h5 className="fw-bold mb-3">{index}. {item.title}</h5>
            <div className="ps-2">
                <LinkRow label=" Link">
                    <span className="text-muted text-break">{item.icsLink}</span>
                    <button
                        className="btn btn-link p-0 text-decoration-none"
                        onClick={handleCopy}
                        style={{ verticalAlign: 'baseline' }}
                    >
                        {copyButtonText}
                    </button>
                </LinkRow>
                {/* {item.otherLinks.map(link => (
                    <LinkRow label={link.name} key={link.name}>
                        <button
                            className="btn btn-link p-0 text-decoration-none"
                            onClick={() => handleAddLink(link.name)}
                            style={{ verticalAlign: 'baseline' }}
                        >
                            Add
                        </button>
                    </LinkRow>
                ))} */}
            </div>
        </div>
    );
};


// --- Komponen Utama Halaman ---
const IcalendarPage = () => {
    // 1. Ambil data, status loading, dan status error dari hook
    const { data: roomTypeResponse, isFetching, isError } = useGetAllRoomType({ all: true });

    // 2. Gunakan useMemo untuk memetakan data secara efisien
    const icsLinkData = useMemo(() => {
        // Jika tidak ada data atau data array kosong, kembalikan array kosong
        if (!roomTypeResponse?.data) {
            return [];
        }

        // Data dummy untuk 'otherLinks' yang akan digunakan berulang kali
        const otherLinksDummy: OtherLink[] = [
            { name: "Airbnb Link" },
            { name: "Google Link" },
            { name: "Trip Advisor Link" },
            { name: "Home Away / VRBO Link" },
            { name: "Intervilla Link" },
        ];

        // Petakan setiap roomType menjadi format yang dibutuhkan IcsLinkItemData
        return roomTypeResponse.data.map((roomType: RoomType) => {
            // Asumsi: `property_id` dari room type pertama bisa digunakan untuk parameter 'p'
            // Atau jika 'p' adalah statis, biarkan seperti di bawah.
            // Di sini kita gunakan nilai statis '4178' dan '15113' sesuai contoh Anda.
            const versionPrefix = 'v3';
            const propertyId = roomType.property_id ?? '';

            return {
                id: roomType.id,
                title: `${roomType.name}`, // Menggunakan nama dari API
                icsLink: `${import.meta.env.VITE_API_URL}/${versionPrefix}/export-icalendar/${propertyId}/${roomType.id}?timestamp=${Date.now()}`,
                otherLinks: otherLinksDummy,
            };
        });
    }, [roomTypeResponse]); // Dependensi: Hanya akan kalkulasi ulang jika roomTypeResponse berubah

    // 3. Fungsi untuk merender konten berdasarkan state
    const renderContent = () => {
        if (isFetching) {
            return (
                <div className="text-center p-5">
                    <Spinner animation="border" variant="primary" />
                    <p className="mt-2">Loading Room Types...</p>
                </div>
            );
        }

        if (isError) {
            return (
                <Alert variant="danger" className="m-4">
                    <strong>Error:</strong> Failed to load room type data. Please try again later.
                </Alert>
            );
        }

        if (icsLinkData.length === 0) {
            return (
                <Alert variant="info" className="m-4 text-center">
                    No room types found.
                </Alert>
            );
        }

        return icsLinkData.map((item, index) => (
            <IcsLinkItem key={item.id} item={item} index={index + 1} />
        ));
    };

    return (
        <AdminLayout>
            <Container fluid className="p-3 p-md-4 bg-light">
                <h1 className="mb-4">iCalendar Links</h1>
                <Card>
                    <Card.Body className="p-0">
                        {renderContent()}
                    </Card.Body>
                </Card>
            </Container>
        </AdminLayout>
    );
}

export default IcalendarPage;